import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Timeout from 'await-timeout';
import { COPY_STRINGS } from '../../../constants/translatedCopy';
import { getRandomArrayElement } from '../../../library';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import QuizLayout from '../QuizLayout';
import QuizImageViewer from '../QuizImageViewer';

function makeCancellable(promise) {
  let isCancelled = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    promise
      .then((val) => (isCancelled ? reject({ isCancelled }) : resolve(val)))
      .catch((error) => (isCancelled ? reject({ isCancelled }) : reject(error)));
  });

  return {
    promise: wrappedPromise,
    cancel() {
      isCancelled = true;
    },
  };
}

const promptTimer = new Timeout();
const PROMPT_TIMEOUT = 10000;
const questionTimer = new Timeout();
const QUESTION_TIMEOUT = 10000;

export default class MultipleChoice extends React.Component {
  isMounted = true;

  constructor(props){
    super(props);
    this.cancellablePromises = [];
    this.state = {
      selected: [],
      tries: 0,
      selectionHistory: [],
      correct: false,
      highlighted: null,
      lastSelectedEncouragementSaid: null,
      optionsDisabled: false,
      speakingQuestion: false,
    };
    this.startPromptTimer = this.startPromptTimer.bind(this);
    this.startQuestionTimer = this.startQuestionTimer.bind(this);
    this.onDoneSpeakingQuestion = this.onDoneSpeakingQuestion.bind(this);
    this.onDoneSpeakingSelection = this.onDoneSpeakingSelection.bind(this);
  }

  componentDidMount(){
    this.isMounted = true;
    if(this.props.introText){
      this.props.speech.abortSpeech(() => {
        this.props.speech.speak({
          textToSpeak: this.props.introText,
          onEnd: () => this.speakQuestion(this.onDoneSpeakingQuestion),
        });
      });
    }else {
      //When no introduction, try checking if we're reading questions
      this.speakQuestion(this.onDoneSpeakingQuestion);
    }
  }

  componentWillUnmount(){
    this.isMounted = false;
    this.cancelPromises();
    promptTimer.clear();
    questionTimer.clear();
  }

  cancelPromises(){
    this.cancellablePromises.forEach(p => p.cancel());
  }

  cancellablePromise(p) {
    const cPromise = makeCancellable(p);
    this.cancellablePromises.push(cPromise);
    return cPromise.promise;
  }

  startPromptTimer(){
    promptTimer.set(PROMPT_TIMEOUT).then(() => {
      if(this.state.selected.length == 0){
        if (!this.isMounted) return;
        this.props.speech.speak({
          textToSpeak: getRandomArrayElement(COPY_STRINGS.intervention_strings.comprehensionPrompt),
          onEnd: this.startQuestionTimer,
        });
      }
    });
  }

  startQuestionTimer(){
    questionTimer.set(QUESTION_TIMEOUT).then(() => {
      if (!this.isMounted) return;
      if(this.state.selected.length == 0){
        const response = " "; //make sure that if the student types nothing, the response is blank rather than null. TODO: find a less hacky way to do this
        this.props.updateResults(this.state.tries, this.state.selectionHistory, response, this.state.selected, this.state.correct);
      }
      this.props.questionTimeout();
    });
  }

  isCorrect(selected){
    //If the arrays are not even the same length then it's not correct
    if(this.props.correctAnswer.length != selected.length){
      return false;
    }
    //If the arrays are the same length then compare every selected answer with the correctAnswer array
    for(let answer of selected){
      if(!this.props.correctAnswer.includes(answer))//If we have a wrong answer return false
        return false;
    }
    //If no wrong answers were found then return true
    return true;
  }

  async selectOption(optionSelected){
    promptTimer.clear();
    questionTimer.clear();

    let getNewSelections = () => {
      let newSelections = this.state.selected ? JSON.parse(JSON.stringify(this.state.selected)) : [];
      newSelections.push(optionSelected);
      if(newSelections.length > this.props.correctAnswer.length){
        newSelections.shift();
      }
      return newSelections;
    }

    let handleSelection = () => {
      if(!this.state.selected || !this.state.selected.includes(optionSelected) || this.state.lastSelectedEncouragementSaid !== optionSelected){
        let newSelections = getNewSelections();
        let correct = this.isCorrect(newSelections);
        this.setState({
          selected: newSelections,
          correct: correct,
          selectionHistory: this.state.selectionHistory.concat("selected " + optionSelected),
        });
        return;
      }
    }

    let evaluateSelection = () => {
      const selected = this.state.selected || [];
      this.props.updateResults(this.state.tries, this.state.selectionHistory, optionSelected, selected, this.state.correct, () => {
        this.setState({lastSelectedEncouragementSaid: optionSelected,optionsDisabled:false});
        if (!this.state.correct) {
          this.startQuestionTimer();
        }
      })
    }

    if(this.props.readQuestion){
        this.cancelPromises();
        await new Promise(resolve => this.props.speech.abortSpeech(resolve));

        this.setState({
          optionsDisabled: true,
          highlighted: null,
        },handleSelection);

        if (!this.isMounted) return;
        await this.cancellablePromise(this.props.speech.speakWithPromise({
          textToSpeak: optionSelected,
          skipTranslation: false,
        }));

        evaluateSelection();
    } else {
      handleSelection();
    }
  }

  onDoneSpeakingSelection() {
    this.setState({ optionsDisabled: false, });
  }

  speakQuestion(onEnd=null){
    if (!this.isMounted) return;
    if(!this.props.readQuestion){
      onEnd && onEnd();
      return;
    }

    this.props.speech.abortSpeech(() => {
      if(!this.state.speakingQuestion) {
        this.setState({
          speakingQuestion: true,
        }, () => {
          let title = this.props.questionTitle;
          title = title.replace("____", "<BLANK/>"); //for backward compatibility
          let blank = COPY_STRINGS.blank;
          let speechProps = {
            textToSpeak: '<break time="0.5s"/>' + title.replace("<BLANK/>", "<break time=\"0.2s\" /> " + blank + " <break time=\"0.2s\" />"),
            passThroughSpeechTags: true,
            skipTranslation: false
          };
          if (onEnd) {
            speechProps.onEnd = onEnd;
          }
          if (!this.isMounted) return;
          this.props.speech.speak(speechProps);
        });
      }
    });
  }

  onDoneSpeakingQuestion(){
    if (!this.isMounted) return;
    this.setState({
      speakingQuestion: false,
    }, async () => {
      if(this.props.readQuestion){
        await this.cancellablePromise(this.speakAllOptions());
      }
      this.startPromptTimer();
    });
  }

  async speakAllOptions(){
    if (!this.isMounted) return;
    const textToSpeak = this.props.options.map((option, index) => {
      const isLastOption = index >= this.props.options.length - 1;
      return `<break time="0.2s"/>${isLastOption ? COPY_STRINGS.or : ''}<prosody rate="95%">${option}</prosody>.`
    }).join('');

    await this.cancellablePromise(
      this.props.speech.speakWithPromise({
        textToSpeak: textToSpeak,
        passThroughSpeechTags: true,
        skipTranslation: false,
      })
    );
  }

  getOptions(){
    const optionsContainerClasses = cx({
      "multiple-options": true,
      "flat": this.props.flat,
      "keepTall": this.props.keepTall,
    });
    let optionClasses = {
      "labelFontOverride": true,
      "option": true,
    };

    const accessibleOptionGroupAttributes = {
      role: "radiogroup",
      "aria-required": true,
      "aria-label": "options",
    }
    const getAccessibleOptionAttributes = option => {
      return {
        ...doNotUseAccessibleStaticElementInteractions({onClick: onOptionClick.bind(this, option)}),
        role: "radio",
        "aria-label": option,
        "aria-checked": this.state.selected.includes(option),
      }
    }

    const onOptionClick = this.state.optionsDisabled ? () => {} : this.selectOption;
    if(this.props.flat){
      return(
        <div className={optionsContainerClasses} {...accessibleOptionGroupAttributes}>
          {this.props.options.map((option, index) => {
            optionClasses.selected = this.state.selected.includes(option);
            optionClasses.highlightedOption = optionClasses.selected;
            optionClasses.inactive = !optionClasses.selected && this.state.optionsDisabled;
            return (
              <span className={cx(optionClasses)} key={option} {...getAccessibleOptionAttributes(option)}>
                {option}
              </span>
            );
          })}
        </div>
      );
    } else {
      return(
        <div className={optionsContainerClasses}>
          <ol className="list" {...accessibleOptionGroupAttributes}>
          {this.props.options.map((option, index) => {
            optionClasses.selected = this.state.selected.includes(option);
            optionClasses.highlightedOption = optionClasses.selected;
            optionClasses.inactive = !optionClasses.selected && this.state.optionsDisabled;
            return (
              <li key={option}>
                <span className={cx(optionClasses)} {...getAccessibleOptionAttributes(option)}>
                  {option}
                </span>
              </li>
            );
          })}
          </ol>
        </div>
      );
    }
  }

  render(){
    const { questionTitle } = this.props;
    let title = questionTitle;
    title = title.replace("____", "<BLANK/>"); //for backward compatibility
    let splitTitle = title.split("<BLANK/>");
    if(splitTitle.length == 2){
      title = (
        <span>
          {splitTitle[0]}
          <span className={`underline ${false /*this.state.correct*/ ? "correct" : ""}`}>
            {this.state.selected && this.state.selected.length == 1 && this.state.selected[0]}
          </span>
           {splitTitle[1]}
        </span>);
    } else {
      title = title.replace("<BLANK/>", "_______");
    }

    let titleClasses = cx({
      "question-title": true,
      "labelFontOverride": true,
      "clickable": this.props.readQuestion,
    });
    return (
      <div className="multiple-wrapper">
        <QuizLayout
          left={this.props.image && <div>
            <QuizImageViewer image={this.props.image} />
            <div className={titleClasses} {...doNotUseAccessibleStaticElementInteractions({ onClick: this.speakQuestion.bind(this, null) })}>
          {title}
        </div>
          </div>}
          right={this.props.image && this.getOptions()}
          top={!this.props.image && <div className={titleClasses} {...doNotUseAccessibleStaticElementInteractions({ onClick: this.speakQuestion.bind(this, null) })}>
            {title}
          </div>}
          bottom={!this.props.image && this.getOptions()}
        />
      </div>
    );
  }
}

MultipleChoice.propTypes = {
  options: PropTypes.array.isRequired,
  correctAnswer: PropTypes.array.isRequired,
  questionTitle: PropTypes.string,
  updateResults: PropTypes.func.isRequired,
};